// The integrations catalogue (C20, D-146): everything Voxowa might connect to, as one typed list, so adding an
// integration or changing its status is a change to this file and its logo (public/logos/<key>.svg, or .png when
// the company has no SVG; sources in docs/integration-logos.md). Each tile says what the integration does in Voxowa;
// opening it adds the details below. Facebook Lead Ads (D-148), MyOperator (D-147) and JustDial (by webhook, D-158)
// connect; everything else waits for requests, which decide what gets built. Never say partner, certified or official
// here.

export type IntegrationStatus = "connected" | "available" | "building" | "request";
export type Category =
  | "lead_sources"
  | "telephony"
  | "crms"
  | "whatsapp"
  | "ai_assistants"
  | "productivity"
  | "payments";
/** IN India, GCC the Gulf, US the United States and Canada, Global everywhere. */
export type Market = "IN" | "GCC" | "US" | "Global";
export type UseCase = "leads_in" | "calls_out" | "both" | "other";

export interface Integration {
  key: string;
  name: string;
  category: Category;
  markets: Market[];
  status: IntegrationStatus;
  /** One line on the tile: what it does in Voxowa. */
  description: string;
  logo: string;
  /** Brings leads in at the company's own webhook address (D-158). */
  webhook?: boolean;
}

/** The categories in the order the screen shows them. */
export const CATEGORIES: { key: Category; title: string; blurb: string }[] = [
  { key: "lead_sources", title: "Lead sources", blurb: "New leads flow into Voxowa" },
  { key: "telephony", title: "Telephony", blurb: "Calls not made from the phone's SIM" },
  { key: "crms", title: "CRMs", blurb: "Voxowa Sync writes call outcomes into your CRM" },
  { key: "whatsapp", title: "WhatsApp", blurb: "Your team's chats with leads, beside their calls" },
  {
    key: "ai_assistants",
    title: "AI assistants (MCP)",
    blurb: "Ask your AI assistant about leads, calls and follow-ups",
  },
  {
    key: "productivity",
    title: "Productivity and automation",
    blurb: "Sheets, calendars, alerts and the rest of your tools",
  },
  { key: "payments", title: "Accounting and payments", blurb: "For when Voxowa is your main CRM" },
];

export const MARKETS: { key: Market | "all"; label: string }[] = [
  { key: "IN", label: "India" },
  { key: "GCC", label: "Gulf" },
  { key: "US", label: "US" },
  { key: "all", label: "All markets" },
];

export const USE_CASES: { key: UseCase; label: string }[] = [
  { key: "leads_in", label: "Bring in leads" },
  { key: "calls_out", label: "Log calls and outcomes" },
  { key: "both", label: "Both" },
  { key: "other", label: "Something else" },
];

export const INTEGRATIONS: Integration[] = [
  {
    key: "facebook-lead-ads",
    name: "Facebook & Instagram Lead Ads",
    category: "lead_sources",
    markets: ["Global"],
    status: "available",
    description: "New leads from your Meta ads arrive instantly",
    logo: "/logos/facebook-lead-ads.svg",
  },
  {
    key: "website-webhook",
    name: "Website forms (webhook)",
    category: "lead_sources",
    markets: ["Global"],
    status: "request",
    description: "Send leads from any form or tool with a webhook",
    logo: "/logos/website-webhook.svg",
  },
  {
    key: "google-ads-leads",
    name: "Google Ads lead forms",
    category: "lead_sources",
    markets: ["Global"],
    status: "request",
    description: "Leads from Google Ads lead forms",
    logo: "/logos/google-ads-leads.png",
  },
  {
    key: "linkedin-lead-gen",
    name: "LinkedIn Lead Gen Forms",
    category: "lead_sources",
    markets: ["Global"],
    status: "request",
    description: "Leads from LinkedIn ads",
    logo: "/logos/linkedin-lead-gen.png",
  },
  {
    key: "indiamart",
    name: "IndiaMART",
    category: "lead_sources",
    markets: ["IN"],
    status: "available",
    description: "Buyer enquiries from IndiaMART",
    logo: "/logos/indiamart.png",
    webhook: true,
  },
  {
    key: "tradeindia",
    name: "TradeIndia",
    category: "lead_sources",
    markets: ["IN"],
    status: "request",
    description: "Buyer enquiries from TradeIndia",
    logo: "/logos/tradeindia.png",
  },
  {
    key: "justdial",
    name: "JustDial",
    category: "lead_sources",
    markets: ["IN"],
    status: "available",
    description: "Enquiries from your JustDial listing",
    logo: "/logos/justdial.svg",
    webhook: true,
  },
  {
    key: "99acres",
    name: "99acres",
    category: "lead_sources",
    markets: ["IN"],
    status: "request",
    description: "Property enquiries from 99acres",
    logo: "/logos/99acres.png",
  },
  {
    key: "magicbricks",
    name: "MagicBricks",
    category: "lead_sources",
    markets: ["IN"],
    status: "request",
    description: "Property enquiries from MagicBricks",
    logo: "/logos/magicbricks.png",
  },
  {
    key: "housing-com",
    name: "Housing.com",
    category: "lead_sources",
    markets: ["IN"],
    status: "request",
    description: "Property enquiries from Housing.com",
    logo: "/logos/housing-com.png",
  },
  {
    key: "nobroker",
    name: "NoBroker",
    category: "lead_sources",
    markets: ["IN"],
    status: "request",
    description: "Enquiries from NoBroker",
    logo: "/logos/nobroker.png",
  },
  {
    key: "cardekho",
    name: "CarDekho",
    category: "lead_sources",
    markets: ["IN"],
    status: "request",
    description: "Car buyer enquiries",
    logo: "/logos/cardekho.png",
  },
  {
    key: "carwale",
    name: "CarWale",
    category: "lead_sources",
    markets: ["IN"],
    status: "request",
    description: "Car buyer enquiries",
    logo: "/logos/carwale.png",
  },
  {
    key: "shiksha",
    name: "Shiksha",
    category: "lead_sources",
    markets: ["IN"],
    status: "request",
    description: "Admission enquiries",
    logo: "/logos/shiksha.png",
  },
  {
    key: "collegedunia",
    name: "Collegedunia",
    category: "lead_sources",
    markets: ["IN"],
    status: "request",
    description: "Admission enquiries",
    logo: "/logos/collegedunia.png",
  },
  {
    key: "sulekha",
    name: "Sulekha",
    category: "lead_sources",
    markets: ["IN"],
    status: "request",
    description: "Service enquiries",
    logo: "/logos/sulekha.svg",
  },
  {
    key: "bayut",
    name: "Bayut",
    category: "lead_sources",
    markets: ["GCC"],
    status: "request",
    description: "Property enquiries from Bayut",
    logo: "/logos/bayut.png",
  },
  {
    key: "property-finder",
    name: "Property Finder",
    category: "lead_sources",
    markets: ["GCC"],
    status: "request",
    description: "Property enquiries from Property Finder",
    logo: "/logos/property-finder.png",
  },
  {
    key: "dubizzle",
    name: "Dubizzle",
    category: "lead_sources",
    markets: ["GCC"],
    status: "request",
    description: "Enquiries from Dubizzle",
    logo: "/logos/dubizzle.png",
  },
  {
    key: "zillow",
    name: "Zillow",
    category: "lead_sources",
    markets: ["US"],
    status: "request",
    description: "Buyer and seller leads from Zillow",
    logo: "/logos/zillow.svg",
  },
  {
    key: "realtor-com",
    name: "Realtor.com",
    category: "lead_sources",
    markets: ["US"],
    status: "request",
    description: "Leads from Realtor.com",
    logo: "/logos/realtor-com.png",
  },
  {
    key: "myoperator",
    name: "MyOperator",
    category: "telephony",
    markets: ["IN"],
    status: "available",
    description: "Calls and recordings from your MyOperator numbers, analysed like SIM calls",
    logo: "/logos/myoperator.png",
  },
  {
    key: "exotel",
    name: "Exotel",
    category: "telephony",
    markets: ["IN"],
    status: "request",
    description: "Calls and recordings from Exotel",
    logo: "/logos/exotel.svg",
  },
  {
    key: "knowlarity",
    name: "Knowlarity",
    category: "telephony",
    markets: ["IN"],
    status: "request",
    description: "Calls and recordings from Knowlarity",
    logo: "/logos/knowlarity.png",
  },
  {
    key: "ozonetel",
    name: "Ozonetel",
    category: "telephony",
    markets: ["IN"],
    status: "request",
    description: "Calls and recordings from Ozonetel",
    logo: "/logos/ozonetel.png",
  },
  {
    key: "tata-tele",
    name: "Tata Tele Business",
    category: "telephony",
    markets: ["IN"],
    status: "request",
    description: "Calls from Tata Tele cloud numbers",
    logo: "/logos/tata-tele.svg",
  },
  {
    key: "airtel-iq",
    name: "Airtel IQ",
    category: "telephony",
    markets: ["IN"],
    status: "request",
    description: "Calls from Airtel IQ numbers",
    logo: "/logos/airtel-iq.svg",
  },
  {
    key: "servetel",
    name: "Servetel",
    category: "telephony",
    markets: ["IN"],
    status: "request",
    description: "Calls and recordings from Servetel",
    logo: "/logos/servetel.svg",
  },
  {
    key: "acefone",
    name: "Acefone",
    category: "telephony",
    markets: ["IN"],
    status: "request",
    description: "Calls and recordings from Acefone",
    logo: "/logos/acefone.svg",
  },
  {
    key: "twilio",
    name: "Twilio",
    category: "telephony",
    markets: ["Global"],
    status: "request",
    description: "Calls from Twilio numbers",
    logo: "/logos/twilio.png",
  },
  {
    key: "aircall",
    name: "Aircall",
    category: "telephony",
    markets: ["US", "GCC"],
    status: "request",
    description: "Calls and recordings from Aircall",
    logo: "/logos/aircall.svg",
  },
  {
    key: "ringcentral",
    name: "RingCentral",
    category: "telephony",
    markets: ["US"],
    status: "request",
    description: "Calls and recordings from RingCentral",
    logo: "/logos/ringcentral.png",
  },
  {
    key: "dialpad",
    name: "Dialpad",
    category: "telephony",
    markets: ["US"],
    status: "request",
    description: "Calls and recordings from Dialpad",
    logo: "/logos/dialpad.png",
  },
  {
    key: "zoho-crm",
    name: "Zoho CRM",
    category: "crms",
    markets: ["IN", "GCC", "Global"],
    status: "request",
    description: "Call summaries, next steps and follow-ups written into Zoho CRM",
    logo: "/logos/zoho-crm.svg",
  },
  {
    key: "bigin",
    name: "Bigin by Zoho",
    category: "crms",
    markets: ["IN", "GCC"],
    status: "request",
    description: "Call outcomes written into Bigin",
    logo: "/logos/bigin.svg",
  },
  {
    key: "leadsquared",
    name: "LeadSquared",
    category: "crms",
    markets: ["IN"],
    status: "request",
    description: "Call outcomes written into LeadSquared",
    logo: "/logos/leadsquared.png",
  },
  {
    key: "freshsales",
    name: "Freshsales",
    category: "crms",
    markets: ["IN", "GCC", "Global"],
    status: "request",
    description: "Call outcomes written into Freshsales",
    logo: "/logos/freshsales.png",
  },
  {
    key: "hubspot",
    name: "HubSpot",
    category: "crms",
    markets: ["Global"],
    status: "request",
    description: "Call outcomes written into HubSpot",
    logo: "/logos/hubspot.svg",
  },
  {
    key: "salesforce",
    name: "Salesforce",
    category: "crms",
    markets: ["Global"],
    status: "request",
    description: "Call outcomes written into Salesforce",
    logo: "/logos/salesforce.svg",
  },
  {
    key: "pipedrive",
    name: "Pipedrive",
    category: "crms",
    markets: ["GCC", "US"],
    status: "request",
    description: "Call outcomes written into Pipedrive",
    logo: "/logos/pipedrive.png",
  },
  {
    key: "odoo",
    name: "Odoo CRM",
    category: "crms",
    markets: ["IN", "GCC"],
    status: "request",
    description: "Call outcomes written into Odoo",
    logo: "/logos/odoo.svg",
  },
  {
    key: "kylas",
    name: "Kylas",
    category: "crms",
    markets: ["IN"],
    status: "request",
    description: "Call outcomes written into Kylas",
    logo: "/logos/kylas.png",
  },
  {
    key: "sell-do",
    name: "Sell.Do",
    category: "crms",
    markets: ["IN"],
    status: "request",
    description: "Call outcomes written into Sell.Do",
    logo: "/logos/sell-do.png",
  },
  {
    key: "follow-up-boss",
    name: "Follow Up Boss",
    category: "crms",
    markets: ["US"],
    status: "request",
    description: "Call outcomes written into Follow Up Boss",
    logo: "/logos/follow-up-boss.png",
  },
  {
    key: "lofty",
    name: "Lofty (kvCORE)",
    category: "crms",
    markets: ["US"],
    status: "request",
    description: "Call outcomes written into Lofty",
    logo: "/logos/lofty.png",
  },
  {
    key: "agencyzoom",
    name: "AgencyZoom",
    category: "crms",
    markets: ["US"],
    status: "request",
    description: "Call outcomes written into AgencyZoom",
    logo: "/logos/agencyzoom.png",
  },
  {
    key: "gohighlevel",
    name: "HighLevel",
    category: "crms",
    markets: ["US", "Global"],
    status: "request",
    description: "Call outcomes written into HighLevel",
    logo: "/logos/gohighlevel.png",
  },
  {
    key: "whatsapp-cloud-api",
    name: "WhatsApp Business (Cloud API)",
    category: "whatsapp",
    markets: ["Global"],
    status: "request",
    description: "Read your team's WhatsApp chats with leads",
    logo: "/logos/whatsapp-cloud-api.svg",
  },
  {
    key: "interakt",
    name: "Interakt",
    category: "whatsapp",
    markets: ["IN"],
    status: "request",
    description: "Read lead chats from Interakt",
    logo: "/logos/interakt.svg",
  },
  {
    key: "wati",
    name: "WATI",
    category: "whatsapp",
    markets: ["IN", "GCC"],
    status: "request",
    description: "Read lead chats from WATI",
    logo: "/logos/wati.png",
  },
  {
    key: "aisensy",
    name: "AiSensy",
    category: "whatsapp",
    markets: ["IN"],
    status: "request",
    description: "Read lead chats from AiSensy",
    logo: "/logos/aisensy.png",
  },
  {
    key: "gupshup",
    name: "Gupshup",
    category: "whatsapp",
    markets: ["IN"],
    status: "request",
    description: "Read lead chats from Gupshup",
    logo: "/logos/gupshup.png",
  },
  {
    key: "mcp-claude",
    name: "Claude",
    category: "ai_assistants",
    markets: ["Global"],
    status: "request",
    description: "Ask Claude about your leads, calls and follow-ups",
    logo: "/logos/mcp-claude.svg",
  },
  {
    key: "mcp-chatgpt",
    name: "ChatGPT",
    category: "ai_assistants",
    markets: ["Global"],
    status: "request",
    description: "Ask ChatGPT about your leads, calls and follow-ups",
    logo: "/logos/mcp-chatgpt.svg",
  },
  {
    key: "mcp-gemini",
    name: "Gemini",
    category: "ai_assistants",
    markets: ["Global"],
    status: "request",
    description: "Ask Gemini about your leads, calls and follow-ups",
    logo: "/logos/mcp-gemini.png",
  },
  {
    key: "mcp-copilot",
    name: "Microsoft Copilot",
    category: "ai_assistants",
    markets: ["Global"],
    status: "request",
    description: "Ask Copilot about your leads, calls and follow-ups",
    logo: "/logos/mcp-copilot.svg",
  },
  {
    key: "mcp-perplexity",
    name: "Perplexity",
    category: "ai_assistants",
    markets: ["Global"],
    status: "request",
    description: "Ask Perplexity about your leads, calls and follow-ups",
    logo: "/logos/mcp-perplexity.svg",
  },
  {
    key: "mcp-other",
    name: "Any MCP client",
    category: "ai_assistants",
    markets: ["Global"],
    status: "request",
    description: "Connect any assistant that supports MCP",
    logo: "/logos/mcp-other.svg",
  },
  {
    key: "google-sheets",
    name: "Google Sheets",
    category: "productivity",
    markets: ["Global"],
    status: "request",
    description: "Import leads from and export to a sheet",
    logo: "/logos/google-sheets.png",
  },
  {
    key: "google-calendar",
    name: "Google Calendar",
    category: "productivity",
    markets: ["Global"],
    status: "request",
    description: "Follow-ups and visits appear on your calendar",
    logo: "/logos/google-calendar.png",
  },
  {
    key: "outlook-calendar",
    name: "Outlook Calendar",
    category: "productivity",
    markets: ["Global"],
    status: "request",
    description: "Follow-ups and visits appear on your calendar",
    logo: "/logos/outlook-calendar.svg",
  },
  {
    key: "zapier",
    name: "Zapier",
    category: "productivity",
    markets: ["Global"],
    status: "request",
    description: "Connect Voxowa to thousands of apps",
    logo: "/logos/zapier.svg",
  },
  {
    key: "pabbly-connect",
    name: "Pabbly Connect",
    category: "productivity",
    markets: ["IN", "Global"],
    status: "request",
    description: "Connect Voxowa to your other apps",
    logo: "/logos/pabbly-connect.svg",
  },
  {
    key: "make",
    name: "Make",
    category: "productivity",
    markets: ["Global"],
    status: "request",
    description: "Connect Voxowa to your other apps",
    logo: "/logos/make.svg",
  },
  {
    key: "slack",
    name: "Slack",
    category: "productivity",
    markets: ["Global"],
    status: "request",
    description: "Hot-lead and missed-follow-up alerts in Slack",
    logo: "/logos/slack.png",
  },
  {
    key: "microsoft-teams",
    name: "Microsoft Teams",
    category: "productivity",
    markets: ["Global"],
    status: "request",
    description: "Hot-lead and missed-follow-up alerts in Teams",
    logo: "/logos/microsoft-teams.svg",
  },
  {
    key: "gmail",
    name: "Gmail",
    category: "productivity",
    markets: ["Global"],
    status: "request",
    description: "Send follow-up emails from Voxowa",
    logo: "/logos/gmail.png",
  },
  {
    key: "razorpay",
    name: "Razorpay",
    category: "payments",
    markets: ["IN"],
    status: "request",
    description: "Send payment links to leads",
    logo: "/logos/razorpay.svg",
  },
  {
    key: "tally",
    name: "Tally",
    category: "payments",
    markets: ["IN"],
    status: "request",
    description: "Turn won deals into invoices in Tally",
    logo: "/logos/tally.png",
  },
  {
    key: "zoho-books",
    name: "Zoho Books",
    category: "payments",
    markets: ["IN", "GCC"],
    status: "request",
    description: "Turn won deals into invoices in Zoho Books",
    logo: "/logos/zoho-books.svg",
  },
];

/** What opening an integration shows: what it does in Voxowa, and what the company needs for it. */
export interface Details {
  does: string[];
  needs: string;
}

/** "A" or "An" before a name: "An Exotel account". */
const an = (name: string) => (/^[aeiou]/i.test(name) ? "An" : "A");

const BY_CATEGORY: Record<Category, (name: string) => Details> = {
  lead_sources: (name) => ({
    does: [
      `New enquiries from ${name} arrive in Voxowa as leads, with where they came from.`,
      "Each lead goes to a telecaller by your assignment rule, at the top of their Today list.",
      "An enquiry from a number you already have joins that lead instead of making a second one.",
    ],
    needs: `${an(name)} ${name} account that receives your enquiries.`,
  }),
  telephony: (name) => ({
    does: [
      `Calls on your ${name} numbers land on the right customer, just like SIM calls.`,
      "Their recordings get the same AI notes, outcome card and follow-ups.",
      `Works for telecallers on iPhones too, since the recording comes from ${name}.`,
    ],
    needs: `${an(name)} ${name} account with API access.`,
  }),
  crms: (name) => ({
    does: [
      `After each call, its summary, outcome and next step are written to the customer in ${name}.`,
      `Follow-ups set in Voxowa show up in ${name} too.`,
      `Keep ${name} as your main CRM while your team calls from Voxowa.`,
    ],
    needs: `${an(name)} ${name} account, connected by its owner or an admin.`,
  }),
  whatsapp: (name) => ({
    does: [
      "Your team's WhatsApp chats with leads show on each lead's page, beside their calls.",
      "AI notes and follow-ups take the chats into account.",
    ],
    needs: `A WhatsApp Business number on ${name}.`,
  }),
  ai_assistants: (name) => ({
    does: [
      `Ask ${name} about your leads, calls and follow-ups in plain words.`,
      "For example: “Which hot leads did nobody call yesterday?”",
      `For owners, and read-only: ${name} can look but can't change anything in Voxowa.`,
    ],
    needs: `${an(name)} ${name} plan that can connect apps through MCP.`,
  }),
  productivity: (name) => ({ does: [], needs: `${an(name)} ${name} account.` }),
  payments: (name) => ({ does: [], needs: `${an(name)} ${name} account.` }),
};

const syncs = (name: string): string[] => [
  `Send new leads, calls and outcomes from Voxowa to the apps ${name} connects to.`,
  `Bring leads in from any of those apps.`,
];
const alerts = (name: string): string[] => [
  `A message in ${name} when a hot lead comes in or a follow-up is missed.`,
  "Choose the channel for each alert.",
];
const calendar = (name: string): string[] => [
  `Follow-ups and site visits appear on each person's ${name}.`,
  `A follow-up moved in Voxowa moves on the calendar too.`,
];

/** Where an integration says more than its category does. */
const OVERRIDES: Record<string, Partial<Details>> = {
  "facebook-lead-ads": {
    does: [
      "A lead who fills your Facebook or Instagram lead form reaches the right telecaller within a minute.",
      "Pick the Pages and forms to bring in; the answers to your questions are saved with the lead.",
      "A repeat enquiry from the same number joins the lead it already has.",
      "When you connect, you can bring in the leads of the last 90 days.",
    ],
    needs: "A Facebook login that can advertise for your Page (a Page admin can).",
  },
  justdial: {
    does: [
      "Each enquiry JustDial sends reaches a telecaller within a minute: a new customer, or the one Voxowa already has.",
      "What the customer looked for, their area and the rest of what JustDial sends are kept with the enquiry.",
      "You get your own address to give JustDial; until its first lead arrives, it says Waiting for the first lead.",
    ],
    needs: "A JustDial listing that gets enquiries, and JustDial sending them to your address: ask your JustDial " +
      "account manager.",
  },
  indiamart: {
    does: [
      "Each buyer enquiry IndiaMART pushes reaches a telecaller within a minute: a new customer, or the one Voxowa " +
      "already has.",
      "The product they asked about, their message, company and city are kept with the enquiry.",
      "Every morning Voxowa also reads the last day's leads with your Pull API key, so a lead the push missed still " +
      "comes in. None comes in twice.",
    ],
    needs: "IndiaMART's Leads API add-on, on a paid IndiaMART plan, which gives you the Push API and the Pull API key.",
  },
  "google-ads-leads": { needs: "A Google Ads account running lead form ads." },
  "linkedin-lead-gen": { needs: "A LinkedIn ads account running Lead Gen Forms." },
  "website-webhook": {
    does: [
      "Send leads from any website form or tool that can call a webhook.",
      "Each lead is assigned and matched to existing ones like any other.",
    ],
    needs: "A form or tool that can send a webhook.",
  },
  myoperator: {
    does: [
      "A call your MyOperator IVR rings a telecaller's phone with shows the IVR's number; Voxowa finds the real caller in MyOperator's call log and puts the call on that customer, with its recording and notes.",
      "A missed IVR call becomes a missed call from that customer, so it gets its follow-up.",
      "Your MyOperator users are matched to your telecallers by phone number.",
      "Voxowa looks each time a phone brings in an IVR call, and every three hours for anything missed.",
    ],
    needs:
      "Your MyOperator Public API token (in MyOperator: Manage → API integration → Public API token), and the numbers your IVR rings your team from.",
  },
  servetel: { needs: "A Servetel (now Acefone) account with API access." },
  "whatsapp-cloud-api": { needs: "A WhatsApp Business number on Meta's Cloud API." },
  "mcp-other": { needs: "Any AI assistant that can connect apps through MCP." },
  "google-sheets": {
    does: [
      "Import leads from a Google Sheet, and bring in new rows as they are added.",
      "Export leads, calls and outcomes to a sheet.",
    ],
    needs: "A Google account with access to the sheet.",
  },
  "google-calendar": { does: calendar("Google Calendar"), needs: "A Google account for each person." },
  "outlook-calendar": {
    does: calendar("Outlook Calendar"),
    needs: "A Microsoft 365 or Outlook account for each person.",
  },
  zapier: { does: syncs("Zapier") },
  "pabbly-connect": { does: syncs("Pabbly Connect") },
  make: { does: syncs("Make") },
  slack: { does: alerts("Slack"), needs: "A Slack workspace." },
  "microsoft-teams": { does: alerts("Teams"), needs: "Microsoft Teams for your company." },
  gmail: {
    does: [
      "Send follow-up emails to leads from Voxowa, from your own Gmail address.",
      "Emails sent show on the lead's page.",
    ],
    needs: "A Gmail or Google Workspace account.",
  },
  razorpay: {
    does: ["Send a payment link to a lead from Voxowa.", "See on the lead's page when it is paid."],
  },
  tally: {
    does: ["Turn a won deal into an invoice in Tally.", "The customer's details go across with it."],
    needs: "TallyPrime for your company.",
  },
  "zoho-books": {
    does: ["Turn a won deal into an invoice in Zoho Books.", "The customer's details go across with it."],
  },
};

export function detailsOf(i: Integration): Details {
  const base = BY_CATEGORY[i.category](i.name);
  const own = OVERRIDES[i.key] ?? {};
  const does = own.does ?? base.does;
  return { does: does.length ? does : [`${i.description}.`], needs: own.needs ?? base.needs };
}

/** The market a company sees first, from its country: India, the Gulf or the US; anywhere else sees them all. */
export function marketFor(country: string | null | undefined): Market | "all" {
  const c = (country ?? "").toUpperCase();
  if (c === "IN") return "IN";
  if (["AE", "SA", "QA", "KW", "BH", "OM"].includes(c)) return "GCC";
  if (c === "US" || c === "CA") return "US";
  return "all";
}

export const MARKET_LABEL: Record<Market, string> = {
  IN: "India",
  GCC: "the Gulf",
  US: "the US and Canada",
  Global: "everywhere",
};

/** Where it works, in words: "Everywhere", "India", "India and the Gulf". */
export function marketsLabel(markets: Market[]): string {
  if (markets.includes("Global")) return "Everywhere";
  const words = markets.map((m) => MARKET_LABEL[m]);
  const text = words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}` : words[0];
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const STATUS_ORDER: IntegrationStatus[] = ["connected", "available", "building", "request"];

/** The integrations to show: in the chosen market (Global ones always), category and search, each category's
 * connected ones first, then ready to connect, coming soon, and the rest, in catalogue order within each. */
export function visibleIntegrations(
  all: Integration[],
  filter: { query?: string; category?: Category | "all"; market?: Market | "all" },
  statusOf: (i: Integration) => IntegrationStatus = (i) => i.status,
): Integration[] {
  const q = (filter.query ?? "").trim().toLowerCase();
  const title = (c: Category) => CATEGORIES.find((x) => x.key === c)?.title.toLowerCase() ?? "";
  return all
    .map((i, n) => ({ i, n }))
    .filter(({ i }) =>
      (!filter.category || filter.category === "all" || i.category === filter.category) &&
      (!filter.market || filter.market === "all" || i.markets.includes("Global") ||
        i.markets.includes(filter.market)) &&
      (!q || `${i.name} ${i.description} ${title(i.category)}`.toLowerCase().includes(q))
    )
    .sort((a, b) =>
      CATEGORIES.findIndex((c) => c.key === a.i.category) - CATEGORIES.findIndex((c) => c.key === b.i.category) ||
      STATUS_ORDER.indexOf(statusOf(a.i)) - STATUS_ORDER.indexOf(statusOf(b.i)) || a.n - b.n
    )
    .map(({ i }) => i);
}

/** What a "Notify me" on an integration being built asks for, from its kind. */
export function defaultUseCase(category: Category): UseCase {
  return category === "lead_sources"
    ? "leads_in"
    : category === "telephony" || category === "crms"
    ? "calls_out"
    : "other";
}

/** A tile's stand-in when its logo is missing: the name's initials, "ZC" for "Zoho CRM". */
export function initials(name: string): string {
  const words = name.replace(/\(.*?\)/g, "").split(/[^A-Za-z0-9]+/).filter(Boolean);
  return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] ?? "?").slice(0, 2)).toUpperCase();
}

/** What Voxowa needs from a Facebook login, and why (D-148). Not ads_management (D-185): a lead brings its ad's names
 * without it. */
export const FACEBOOK_PERMISSIONS: [string, string][] = [
  ["pages_show_list", "to list your Pages"],
  ["business_management", "to see a Page that a business portfolio owns"],
  ["pages_manage_metadata", "for Facebook to tell Voxowa of each new lead"],
  ["leads_retrieval", "to read the leads"],
  ["pages_read_engagement", "to read the Page"],
  ["pages_manage_ads", "to read the Page's lead forms"],
];

/** The permissions a login lacks, with what each is for; none when Facebook did not say what it gave. */
export function missingPermissions(granted: string[] | undefined): [string, string][] {
  return granted?.length ? FACEBOOK_PERMISSIONS.filter(([p]) => !granted.includes(p)) : [];
}

/** Who gets new customers, as an activity line says it: "Anjali", "in turn: Anjali, Kashish", "nobody". */
function owners(d: Record<string, unknown>): string {
  const names = Array.isArray(d.member_names) ? (d.member_names as string[]) : [];
  switch (d.assign) {
    case "default":
      return "as for all forms";
    case "none":
      return "nobody";
    case "one":
      return names[0] ?? "one person";
    case "split":
      return `in turn: ${names.join(", ")}`;
    default:
      return String(d.assign ?? "");
  }
}

/** One change to an integration in words (D-157), for its Activity tab. */
export function activityText(item: { action: string; detail: Record<string, unknown> }): string {
  const d = item.detail ?? {};
  switch (item.action) {
    case "integration.connected":
      return d.facebook_user
        ? `Connected, with the Facebook login of ${d.facebook_user}`
        : d.users !== undefined
        ? `Connected, with ${d.users} MyOperator users`
        : d.address
        ? "Turned on, with its own address for leads"
        : "Connected";
    case "integration.disconnected":
      return "Stopped: disconnected";
    case "integration.paused":
      return "Paused";
    case "integration.resumed":
      return "Resumed";
    case "integration.error":
      return `Stopped working${d.error ? `: ${d.error}` : ""}`;
    case "integration.recovered":
      return "Working again";
    case "integration.first_event":
      return "First lead received";
    case "integration.status":
      return `Status changed from ${d.from} to ${d.to}`;
    case "leads.unassigned":
      return `Took ${d.count ?? "the"} imported leads off the call lists`;
    case "integration.changed":
      if (d.page_added) return `Added the Page ${d.page_added}`;
      if (d.page_removed) return `Removed the Page ${d.page_removed}`;
      if (d.import_days) {
        return `Brought in the leads of the last ${d.import_days} days${d.assign === false ? ", given to nobody" : ""}`;
      }
      if (d.form) return `Form "${d.form_name ?? d.form}": new customers go to ${owners(d)}`;
      if (d.assign !== undefined) return `New customers go to ${owners(d)}`;
      if ("forms" in d) {
        return Array.isArray(d.forms)
          ? `Chose ${d.forms.length} ${d.forms.length === 1 ? "form" : "forms"} to bring leads from`
          : "Chose to bring leads from every form";
      }
      if (Array.isArray(d.ivr_numbers)) return `IVR numbers: ${(d.ivr_numbers as string[]).join(", ") || "none"}`;
      if (d.address === "renewed") return "Made a new address; the old one stopped working";
      if (d.pull_key === "set") return "Added the Pull API key for the daily read";
      if (d.pull_key === "removed") return "Removed the Pull API key: the daily read stopped";
      if (d.fields && typeof d.fields === "object") return fieldsText(d.fields as Record<string, string>);
      if (d.tried !== undefined) {
        return `Tried ${d.tried} waiting ${d.tried === 1 ? "lead" : "leads"} again: ${d.brought_in ?? 0} came in`;
      }
      return "Settings changed";
    default:
      return item.action;
  }
}

/** The fields chosen for a webhook's leads (D-158), in words. */
function fieldsText(fields: Record<string, string>): string {
  const said = (["phone", "name", "email", "city"] as const)
    .filter((k) => fields[k])
    .map((k) => `${WEBHOOK_FIELD_LABELS[k].toLowerCase()} from "${fields[k]}"`);
  return said.length ? `Fields: ${said.join(", ")}` : "Fields: each found by its usual name";
}

/** What Voxowa reads from a webhook's lead, in the order the owner chooses them. */
export const WEBHOOK_FIELD_LABELS = { phone: "Phone number", name: "Name", email: "Email", city: "City" } as const;
